import { Box, Button, Card, CardContent, Chip, LinearProgress, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import { useAuth } from '../../providers/AuthProvider'
import { useActiveExperiment } from '../../providers/ActiveExperimentProvider'
import { useExperiments } from '../../hooks/useExperiments'
import { useKUs } from '../../hooks/useKUs'
import { SUBJECTS, SUBJECT_META } from '../../data/standards'
import { useStandards, StandardNode, gradeLabel, gradeOrder } from '../../providers/StandardsProvider'
import { Experiment, SamplingStrategy, Subject } from '../../types'

// ── Sampling logic ───────────────────────────────────────────────────────────
type GradeMethod = 'pick' | 'random'
type StdSamplingMethod = 'all' | 'handpick' | 'random' | 'stratified'

const STD_SAMPLING_METHODS: { id: StdSamplingMethod; label: string; desc: string }[] = [
  { id: 'all', label: 'All Standards', desc: 'Every standard in the selected grades' },
  { id: 'handpick', label: 'Hand-Pick', desc: 'Choose specific standards manually' },
  { id: 'random', label: 'Random Sample', desc: 'Randomly select from the pool' },
  { id: 'stratified', label: 'Stratified by Grade', desc: 'Equal sample from each grade' },
]

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function sampleFlat(pool: StandardNode[], count: number): StandardNode[] {
  return shuffle(pool).slice(0, Math.min(count, pool.length))
}

function sampleStratified(
  grades: string[],
  getStds: (g: string) => StandardNode[],
  value: number,
  mode: 'count' | 'pct',
): StandardNode[] {
  const result: StandardNode[] = []
  for (const g of grades) {
    const stds = getStds(g)
    const n = mode === 'pct' ? Math.max(1, Math.round((value / 100) * stds.length)) : Math.min(value, stds.length)
    result.push(...shuffle(stds).slice(0, n))
  }
  return result
}

// ── Experiment creation flow ──────────────────────────────────────────────────
interface CreateFlowProps {
  userId: string
  defaultName: string
  onCancel: () => void
  onDone: (draft: {
    name: string
    research_question: string
    subject: string
    standards: string[]
    grades: string[]
    sampling_strategy: SamplingStrategy
  }) => void
}

function ExperimentCreateFlow({ userId, defaultName, onCancel, onDone }: CreateFlowProps) {
  const { getGrades, getLeafStandards, findStandard } = useStandards()

  const [subject, setSubject] = useState<Subject | null>(null)
  const [selGrades, setSelGrades] = useState<string[]>([])
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [name, setName] = useState(defaultName)
  const [researchQuestion, setResQ] = useState('')
  const [gradeMethod, setGradeMethod] = useState<GradeMethod>('pick')
  const [gradeRandomN, setGradeRandomN] = useState(3)
  const [stdSampling, setStdSampling] = useState<StdSamplingMethod>('handpick')
  const [samplingMode, setSamplingMode] = useState<'count' | 'pct'>('pct')
  const [samplingValue, setSamplingValue] = useState(25)

  const sm = subject ? SUBJECT_META[subject] : null
  const allGrades = subject ? getGrades(subject) || [] : []
  const poolStds: StandardNode[] = subject ? selGrades.flatMap((g) => getLeafStandards(subject, g) || []) : []

  const handleSubjectChange = (s: Subject) => {
    setSubject(s)
    setSelGrades([])
    setPicked(new Set())
    setGradeMethod('pick')
    setStdSampling('handpick')
  }

  const toggleGrade = (g: string) => {
    if (!subject || gradeMethod !== 'pick') return
    const adding = !selGrades.includes(g)
    const next = adding ? [...selGrades, g] : selGrades.filter((x) => x !== g)
    setSelGrades(next)
    if (stdSampling === 'handpick') {
      if (adding) {
        const stds = getLeafStandards(subject, g) || []
        setPicked((prev) => new Set(Array.from(prev).concat(stds.map((s) => s.code!))))
      } else {
        const removed = new Set((getLeafStandards(subject, g) || []).map((s) => s.code!))
        setPicked((prev) => new Set(Array.from(prev).filter((c) => !removed.has(c))))
      }
    }
  }

  const togglePick = (code: string) => {
    setPicked((prev) => {
      const next = new Set(prev)
      next.has(code) ? next.delete(code) : next.add(code)
      return next
    })
  }

  const setGradeAll = (grade: string, selectAll: boolean) => {
    if (!subject) return
    const stds = getLeafStandards(subject, grade) || []
    const codes = new Set(stds.map((s) => s.code!))
    setPicked((prev) => {
      const next = new Set(prev)
      if (selectAll) stds.forEach((s) => next.add(s.code!))
      else codes.forEach((c) => next.delete(c))
      return next
    })
  }

  // Randomly pick grades
  const rollRandomGrades = () => {
    if (!subject) return
    const n = Math.min(gradeRandomN, allGrades.length)
    const picked = shuffle(allGrades).slice(0, n)
    setSelGrades(picked.sort((a, b) => gradeOrder(a) - gradeOrder(b)))
    setPicked(new Set())
  }

  const handleGradeMethodChange = (m: GradeMethod) => {
    setGradeMethod(m)
    setSelGrades([])
    setPicked(new Set())
    if (m === 'random') {
      // Auto-roll on switch to random
      if (subject) {
        const n = Math.min(gradeRandomN, allGrades.length)
        const g = shuffle(allGrades).slice(0, n)
        setSelGrades(g.sort((a, b) => gradeOrder(a) - gradeOrder(b)))
      }
    }
  }

  // Apply standard sampling to populate picked set
  const applyStdSampling = (method?: StdSamplingMethod) => {
    const m = method ?? stdSampling
    if (!subject) return
    if (m === 'handpick') return

    if (m === 'all') {
      setPicked(new Set(poolStds.map((s) => s.code!)))
      return
    }

    if (m === 'random') {
      const count =
        samplingMode === 'pct' ? Math.max(1, Math.round((samplingValue / 100) * poolStds.length)) : samplingValue
      const sample = sampleFlat(poolStds, count)
      setPicked(new Set(sample.map((s) => s.code!)))
      return
    }

    if (m === 'stratified') {
      const getStds = (g: string) => getLeafStandards(subject, g) || []
      const sample = sampleStratified(selGrades, getStds, samplingValue, samplingMode)
      setPicked(new Set(sample.map((s) => s.code!)))
      return
    }
  }

  const handleStdSamplingChange = (m: StdSamplingMethod) => {
    setStdSampling(m)
    if (m === 'handpick') {
      // Reset to all standards in selected grades
      setPicked(new Set(poolStds.map((s) => s.code!)))
      return
    }
    if (selGrades.length > 0) {
      setTimeout(() => applyStdSampling(m), 0)
    }
  }

  // Status line for footer
  const statusLine = useMemo(() => {
    const stdCount = stdSampling === 'all' ? poolStds.length : picked.size
    if (stdCount === 0) return null
    const codes = stdSampling === 'all' ? poolStds.map((s) => s.code!) : Array.from(picked)
    const domains = new Set<string>()
    for (const code of codes) {
      const m = findStandard(code)
      if (m?.domain) domains.add(m.domain)
    }
    const nG = selGrades.length
    const nD = domains.size
    const scope = nG === 1 && nD <= 2 ? 'Focused' : nG <= 2 && nD <= 4 ? 'Moderate' : 'Broad'
    const gradeStr = selGrades.length === 1 ? gradeLabel(selGrades[0]) : `${selGrades.length} grades`
    return `${stdCount} selected · ${gradeStr} · ${nD} domain${nD !== 1 ? 's' : ''} · ${scope}`
  }, [picked, selGrades, stdSampling, poolStds, findStandard]) // eslint-disable-line

  const canCreate =
    subject !== null && selGrades.length > 0 && (stdSampling === 'all' ? poolStds.length > 0 : picked.size > 0)

  const buildSamplingStrategy = (): SamplingStrategy => {
    const base: SamplingStrategy =
      stdSampling === 'all'
        ? { method: 'all' }
        : stdSampling === 'handpick'
        ? { method: 'handpick' }
        : stdSampling === 'random'
        ? {
            method: 'random',
            ...(samplingMode === 'pct' ? { samplePct: samplingValue } : { sampleCount: samplingValue }),
          }
        : {
            method: 'stratified',
            stratifyBy: 'grade' as const,
            ...(samplingMode === 'pct' ? { samplePct: samplingValue } : { perStratum: samplingValue }),
          }
    if (gradeMethod === 'random') {
      base.nGrades = gradeRandomN
    }
    return base
  }

  const finish = () => {
    if (!subject) return
    const finalStds = stdSampling === 'all' ? poolStds.map((s) => s.code!) : Array.from(picked)
    onDone({
      name: name.trim(),
      research_question: researchQuestion.trim(),
      subject,
      standards: finalStds,
      grades: selGrades.slice().sort((a, b) => gradeOrder(a) - gradeOrder(b)),
      sampling_strategy: buildSamplingStrategy(),
    })
  }

  // Shared section label style
  const sectionLabel = {
    fontWeight: 700,
    color: 'text.secondary',
    fontSize: 10.5,
    letterSpacing: '0.07em',
    textTransform: 'uppercase' as const,
    display: 'block',
    mb: 1,
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 2,
          bgcolor: 'background.paper',
          borderBottom: `3px solid ${sm?.color ?? '#e2e8f0'}`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ ...sectionLabel, mb: 0.375 }}>Experiment Name</Typography>
        <Box
          component="input"
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          placeholder=""
          sx={{
            display: 'block',
            width: '100%',
            border: 'none',
            outline: 'none',
            fontSize: 20,
            fontWeight: 800,
            color: 'primary.main',
            fontFamily: 'inherit',
            bgcolor: 'transparent',
            mb: 1.5,
            '&::placeholder': { color: '#cbd5e1' },
          }}
        />
        <Typography sx={{ ...sectionLabel, mb: 0.375 }}>Research Question</Typography>
        <Box
          component="input"
          value={researchQuestion}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResQ(e.target.value)}
          placeholder="What question is this experiment designed to answer?"
          sx={{
            display: 'block',
            width: '100%',
            border: 'none',
            outline: 'none',
            fontSize: 14,
            fontWeight: 500,
            color: '#1e293b',
            fontFamily: 'inherit',
            bgcolor: 'transparent',
            lineHeight: 1.6,
            '&::placeholder': { color: '#94a3b8' },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* ── Subject ── */}
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.75,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={sectionLabel}>Subject</Typography>
          <Box sx={{ display: 'flex', gap: 0.625 }}>
            {SUBJECTS.map((s) => {
              const active = subject === s
              const meta = SUBJECT_META[s]
              return (
                <Box
                  key={s}
                  onClick={() => handleSubjectChange(s)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 20,
                    cursor: 'pointer',
                    border: '1.5px solid',
                    borderColor: active ? meta.color : '#e2e8f0',
                    bgcolor: active ? meta.bg : 'background.paper',
                    color: active ? meta.color : '#94a3b8',
                    fontWeight: active ? 700 : 500,
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    transition: 'all 0.12s',
                    userSelect: 'none',
                  }}
                >
                  <Box component="span" sx={{ fontSize: 13, lineHeight: 1 }}>
                    {meta.icon}
                  </Box>
                  <Box component="span">{s}</Box>
                </Box>
              )
            })}
          </Box>
        </Box>

        {/* ── Grades ── */}
        {subject && sm && (
          <>
            {/* Grade selection method */}
            <Box
              sx={{
                px: 2.5,
                pt: 1.75,
                pb: 1.5,
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={sectionLabel}>Grades</Typography>
              {/* Pick vs Random toggle */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1.25 }}>
                {[
                  { id: 'pick' as GradeMethod, label: 'Pick specific grades' },
                  { id: 'random' as GradeMethod, label: 'Random grades' },
                ].map((opt) => {
                  const active = gradeMethod === opt.id
                  return (
                    <Box
                      key={opt.id}
                      onClick={() => handleGradeMethodChange(opt.id)}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 20,
                        cursor: 'pointer',
                        border: '1.5px solid',
                        fontSize: 11,
                        fontWeight: 600,
                        userSelect: 'none',
                        borderColor: active ? sm.color : '#e2e8f0',
                        bgcolor: active ? sm.bg : 'background.paper',
                        color: active ? sm.color : '#94a3b8',
                        transition: 'all 0.12s',
                      }}
                    >
                      {opt.label}
                    </Box>
                  )
                })}
              </Box>

              {gradeMethod === 'pick' ? (
                /* Manual grade pills */
                <Box sx={{ display: 'flex', gap: 0.625, flexWrap: 'wrap' }}>
                  {allGrades.map((g) => {
                    const active = selGrades.includes(g)
                    const n = (getLeafStandards(subject, g) || []).length
                    return (
                      <Box
                        key={g}
                        onClick={() => toggleGrade(g)}
                        sx={{
                          px: 1.375,
                          py: 0.5,
                          borderRadius: 20,
                          cursor: 'pointer',
                          border: '1.5px solid',
                          borderColor: active ? sm.color : '#e2e8f0',
                          bgcolor: active ? sm.color : 'background.paper',
                          color: active ? '#fff' : '#64748b',
                          fontWeight: active ? 700 : 400,
                          fontSize: 12,
                          transition: 'all 0.12s',
                          userSelect: 'none',
                          boxShadow: active ? `0 1px 4px ${sm.color}40` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {gradeLabel(g)}
                        <Box component="span" sx={{ fontSize: 10, opacity: active ? 0.8 : 0.5 }}>
                          ({n})
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              ) : (
                /* Random grades config */
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    component="input"
                    type="number"
                    value={gradeRandomN}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setGradeRandomN(Math.max(1, Math.min(allGrades.length, Number(e.target.value))))
                    }
                    min={1}
                    max={allGrades.length}
                    sx={{
                      width: 48,
                      fontSize: 12,
                      textAlign: 'center',
                      fontWeight: 600,
                      border: '1px solid #e2e8f0',
                      borderRadius: 1,
                      p: '3px 6px',
                      color: 'primary.main',
                      outline: 'none',
                      fontFamily: 'inherit',
                      bgcolor: 'background.paper',
                      '&:focus': { borderColor: sm.color },
                    }}
                  />
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>of {allGrades.length} grades</Typography>
                  <Box
                    onClick={rollRandomGrades}
                    sx={{
                      px: 1.25,
                      py: 0.3,
                      borderRadius: 1,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: `1px solid ${sm.color}30`,
                      color: sm.color,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: sm.bg },
                      transition: 'all 0.1s',
                      userSelect: 'none',
                    }}
                  >
                    Re-roll
                  </Box>
                  {selGrades.length > 0 && (
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', ml: 0.5 }}>
                      {selGrades.map((g) => gradeLabel(g)).join(', ')}
                    </Typography>
                  )}
                </Box>
              )}

              {selGrades.length === 0 && gradeMethod === 'pick' && (
                <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 1.25, display: 'block' }}>
                  Select one or more grades to continue.
                </Typography>
              )}
            </Box>

            {/* Standard sampling method (shown once grades are selected) */}
            {selGrades.length > 0 && (
              <Box
                sx={{
                  px: 2.5,
                  pt: 1.75,
                  pb: 1.5,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography sx={sectionLabel}>Standard Selection</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {STD_SAMPLING_METHODS.map((m) => {
                    const active = stdSampling === m.id
                    return (
                      <Box
                        key={m.id}
                        onClick={() => handleStdSamplingChange(m.id)}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 20,
                          cursor: 'pointer',
                          border: '1.5px solid',
                          fontSize: 11,
                          fontWeight: 600,
                          userSelect: 'none',
                          borderColor: active ? sm.color : '#e2e8f0',
                          bgcolor: active ? sm.bg : 'background.paper',
                          color: active ? sm.color : '#94a3b8',
                          transition: 'all 0.12s',
                        }}
                      >
                        {m.label}
                      </Box>
                    )
                  })}
                </Box>
                <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.75 }}>
                  {STD_SAMPLING_METHODS.find((m) => m.id === stdSampling)?.desc}
                </Typography>

                {/* Config for random / stratified */}
                {(stdSampling === 'random' || stdSampling === 'stratified') && (
                  <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        border: '1.5px solid',
                        borderColor: sm.color + '40',
                        borderRadius: 20,
                        overflow: 'hidden',
                      }}
                    >
                      {(['pct', 'count'] as const).map((mode) => (
                        <Box
                          key={mode}
                          onClick={() => setSamplingMode(mode)}
                          sx={{
                            px: 1.25,
                            py: 0.3,
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: 'pointer',
                            bgcolor: samplingMode === mode ? sm.color : 'transparent',
                            color: samplingMode === mode ? '#fff' : sm.color,
                            transition: 'all 0.1s',
                            userSelect: 'none',
                          }}
                        >
                          {mode === 'pct' ? '%' : '#'}
                        </Box>
                      ))}
                    </Box>
                    <Box
                      component="input"
                      type="number"
                      value={samplingValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSamplingValue(Math.max(1, Number(e.target.value)))
                      }
                      min={1}
                      max={samplingMode === 'pct' ? 100 : poolStds.length}
                      sx={{
                        width: 56,
                        fontSize: 12,
                        textAlign: 'center',
                        fontWeight: 600,
                        border: '1px solid #e2e8f0',
                        borderRadius: 1,
                        p: '3px 6px',
                        color: 'primary.main',
                        outline: 'none',
                        fontFamily: 'inherit',
                        bgcolor: 'background.paper',
                        '&:focus': { borderColor: sm.color },
                      }}
                    />
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                      {samplingMode === 'pct'
                        ? `% ${stdSampling === 'stratified' ? 'per grade' : 'of pool'}`
                        : `stds ${stdSampling === 'stratified' ? 'per grade' : 'total'}`}
                    </Typography>
                    <Box
                      onClick={() => applyStdSampling()}
                      sx={{
                        ml: 'auto',
                        px: 1.25,
                        py: 0.3,
                        borderRadius: 1,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: `1px solid ${sm.color}30`,
                        color: sm.color,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: sm.bg },
                        transition: 'all 0.1s',
                        userSelect: 'none',
                      }}
                    >
                      Apply
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {/* ── Standards ── */}
        {selGrades.length > 0 && sm && subject && stdSampling !== 'all' && (
          <Box>
            {/* Toolbar */}
            <Box
              sx={{
                px: 2.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: '#f8fafc',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ ...sectionLabel, mb: 0, flex: 1 }}>
                Standards
                <Box
                  component="span"
                  sx={{ fontWeight: 400, color: '#94a3b8', ml: 0.75, textTransform: 'none', letterSpacing: 0 }}
                >
                  — {picked.size} of {poolStds.length} selected
                </Box>
              </Typography>
              {stdSampling === 'handpick' ? (
                <>
                  {[
                    { label: 'Select all', action: () => setPicked(new Set(poolStds.map((s) => s.code!))) },
                    { label: 'Clear', action: () => setPicked(new Set()), danger: true },
                  ].map(({ label, action, danger }) => (
                    <Box
                      key={label}
                      onClick={action}
                      sx={{
                        px: 1.25,
                        py: 0.3,
                        borderRadius: 1,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        userSelect: 'none',
                        border: '1px solid',
                        borderColor: danger ? '#fca5a5' : `${sm.color}30`,
                        color: danger ? '#dc2626' : sm.color,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: danger ? '#fef2f2' : sm.bg },
                        transition: 'all 0.1s',
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </>
              ) : (
                <Box
                  onClick={() => applyStdSampling()}
                  sx={{
                    px: 1.25,
                    py: 0.3,
                    borderRadius: 1,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    border: `1px solid ${sm.color}30`,
                    color: sm.color,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: sm.bg },
                    transition: 'all 0.1s',
                  }}
                >
                  Re-roll
                </Box>
              )}
            </Box>

            {/* Per-grade groups */}
            {selGrades.map((grade) => {
              const gStds = getLeafStandards(subject, grade) || []
              if (!gStds.length) return null
              const gradeSelected = gStds.filter((s) => picked.has(s.code!)).length
              const allSel = gradeSelected === gStds.length
              return (
                <Box key={grade}>
                  {/* Grade subheader */}
                  <Box
                    sx={{
                      px: 2.5,
                      py: 0.875,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: '#f8fafc',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      borderLeft: `3px solid ${sm.color}`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: sm.color, fontSize: 11.5 }}>
                      {gradeLabel(grade)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
                        {gradeSelected}/{gStds.length}
                      </Typography>
                      <Box
                        onClick={() => setGradeAll(grade, !allSel)}
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: sm.color,
                          cursor: 'pointer',
                          px: 1,
                          py: 0.25,
                          border: `1px solid ${sm.color}40`,
                          borderRadius: 1,
                          userSelect: 'none',
                          '&:hover': { bgcolor: sm.bg },
                          transition: 'background-color 0.1s',
                        }}
                      >
                        {allSel ? 'Clear' : 'All'}
                      </Box>
                    </Box>
                  </Box>

                  {/* Standard rows */}
                  {gStds.map((std, idx) => {
                    const sel = picked.has(std.code!)
                    return (
                      <Box
                        key={std.code}
                        onClick={() => togglePick(std.code!)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2.5,
                          py: 1,
                          cursor: 'pointer',
                          bgcolor: sel ? sm.bg + '90' : 'background.paper',
                          borderBottom: idx < gStds.length - 1 ? '1px solid' : 'none',
                          borderColor: '#f1f5f9',
                          borderLeft: `3px solid ${sel ? sm.color : 'transparent'}`,
                          '&:hover': { bgcolor: sel ? sm.bg : '#f8fafc' },
                          transition: 'all 0.1s',
                        }}
                      >
                        {/* Checkbox */}
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 0.5,
                            flexShrink: 0,
                            bgcolor: sel ? sm.color : 'background.paper',
                            border: '2px solid',
                            borderColor: sel ? sm.color : '#cbd5e1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.1s',
                            boxShadow: sel ? `0 0 0 3px ${sm.color}20` : 'none',
                          }}
                        >
                          {sel && (
                            <Typography sx={{ color: '#fff', fontSize: 9, fontWeight: 900, lineHeight: 1 }}>
                              ✓
                            </Typography>
                          )}
                        </Box>
                        {/* Code */}
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: sel ? sm.color : '#64748b',
                            flexShrink: 0,
                            fontSize: 11,
                            minWidth: 108,
                            fontFamily: 'inherit',
                          }}
                        >
                          {std.code}
                        </Typography>
                        {/* Body */}
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: 12,
                            color: sel ? '#1a202c' : '#64748b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.4,
                          }}
                        >
                          {std.body}
                        </Typography>
                      </Box>
                    )
                  })}
                  <Box sx={{ height: 1, bgcolor: '#e2e8f0' }} />
                </Box>
              )
            })}
          </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={onCancel}
          sx={{
            color: '#94a3b8',
            fontSize: 12,
            minWidth: 0,
            px: 1,
            '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
          }}
        >
          Cancel
        </Button>
        <Typography sx={{ flex: 1, fontSize: 11, color: '#94a3b8' }}>{statusLine || ''}</Typography>
        <Button
          variant="contained"
          disabled={!canCreate}
          onClick={finish}
          sx={{
            bgcolor: sm?.color ?? 'primary.main',
            '&:hover': { bgcolor: (sm?.color ?? '') + 'dd' },
            fontSize: 13,
            fontWeight: 700,
            px: 2.5,
            boxShadow: sm ? `0 2px 8px ${sm.color}40` : 'none',
            '&:disabled': { boxShadow: 'none' },
          }}
        >
          Create Experiment
        </Button>
      </Box>
    </Box>
  )
}

// ── Main experiments page ──────────────────────────────────────────────────────
export default function DatasetsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { activeExperiment, setActiveExperiment } = useActiveExperiment()
  const { experiments, createExperiment, deleteExperiment } = useExperiments(user?.id || '')
  const { fetchStandardsWithKUs } = useKUs()
  const [creating, setCreating] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [stdsWithData, setStdsWithData] = useState<Set<string>>(new Set())

  useEffect(() => {
    fetchStandardsWithKUs().then(setStdsWithData)
  }, [fetchStandardsWithKUs])

  const handleActivate = (e: React.MouseEvent, exp: Experiment) => {
    e.stopPropagation()
    setActiveExperiment(exp)
    navigate('/standards')
  }

  const handleDeactivate = (e: React.MouseEvent) => {
    e.stopPropagation()
    setActiveExperiment(null)
  }

  if (creating) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
        <ExperimentCreateFlow
          userId={user?.id || ''}
          defaultName={`Experiment ${experiments.length + 1}`}
          onCancel={() => setCreating(false)}
          onDone={async (draft) => {
            const exp = await createExperiment(draft)
            setCreating(false)
            if (exp) navigate(`/datasets/${exp.id}`)
          }}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
            Experiments
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Named collections of standards for research
          </Typography>
        </Box>
        <Button variant="contained" size="small" onClick={() => setCreating(true)} sx={{ mt: 0.5 }}>
          + New
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 10 }}>
        {experiments.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 8 }}>
            <Typography sx={{ fontSize: 32, mb: 1.5 }}>🧪</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.75 }}>
              No experiments yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create an experiment to define the scope of your research.
            </Typography>
            <Button variant="contained" onClick={() => setCreating(true)}>
              Create First Experiment
            </Button>
          </Box>
        ) : (
          experiments.map((exp) => {
            const stdIds = exp.standards
            const done = stdIds.filter((s) => stdsWithData.has(s)).length
            const pct = stdIds.length ? Math.round((done / stdIds.length) * 100) : 0
            const sm = SUBJECT_META[exp.subject]
            const isActive = activeExperiment?.id === exp.id

            return (
              <Card
                key={exp.id}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.50' },
                  borderColor: isActive ? '#fde68a' : 'divider',
                  bgcolor: isActive ? '#fffbeb' : undefined,
                }}
                onClick={() => navigate(`/datasets/${exp.id}`)}
              >
                <CardContent sx={{ pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                        {isActive && <Typography sx={{ fontSize: 12 }}>🧪</Typography>}
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: 14 }}>
                          {exp.name}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: 11,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                        }}
                      >
                        {exp.research_question}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: pct === 100 ? '#16a34a' : 'text.secondary', flexShrink: 0, ml: 1 }}
                    >
                      {pct}%
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.875, flexWrap: 'wrap' }}>
                    <Chip
                      label={exp.subject}
                      size="small"
                      sx={{ bgcolor: sm.bg, color: sm.color, fontWeight: 700, height: 20, fontSize: 10 }}
                    />
                    {exp.grades.slice(0, 3).map((g) => (
                      <Chip key={g} label={g} size="small" sx={{ height: 20, fontSize: 10 }} />
                    ))}
                    {exp.grades.length > 3 && (
                      <Chip label={`+${exp.grades.length - 3}`} size="small" sx={{ height: 20, fontSize: 10 }} />
                    )}
                    <Chip label={`${stdIds.length} stds`} size="small" sx={{ height: 20, fontSize: 10 }} />
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      mb: 0.875,
                      bgcolor: 'grey.100',
                      '& .MuiLinearProgress-bar': { bgcolor: pct === 100 ? '#16a34a' : 'primary.main' },
                    }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                      Created {new Date(exp.created_at).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isActive ? (
                        <Box
                          onClick={handleDeactivate}
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#d97706',
                            cursor: 'pointer',
                            px: 0.75,
                            py: 0.25,
                            border: '1px solid #d97706',
                            borderRadius: 1,
                          }}
                        >
                          Deactivate
                        </Box>
                      ) : (
                        <Box
                          onClick={(e) => handleActivate(e, exp)}
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'primary.main',
                            cursor: 'pointer',
                            px: 0.75,
                            py: 0.25,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                          }}
                        >
                          Activate
                        </Box>
                      )}
                      <Box
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteId(exp.id)
                        }}
                        sx={{ fontSize: 11, color: 'error.main', cursor: 'pointer', px: 0.75 }}
                      >
                        Delete
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )
          })
        )}
      </Box>

      <ConfirmDialog
        open={Boolean(deleteId)}
        title="Delete experiment?"
        message="This will permanently delete the experiment. KUs are not affected."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => {
          if (deleteId) deleteExperiment(deleteId)
          setDeleteId(null)
        }}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  )
}
